import { argon2id } from 'hash-wasm';
import * as sodium from 'libsodium-wrappers-sumo';

// Key management for local-first budget app
export class KeyManager {
  private static instance: KeyManager;
  private dataEncryptionKey: Uint8Array | null = null;
  private isInitialized = false;
  private lockTimeout: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): KeyManager {
    if (!KeyManager.instance) {
      KeyManager.instance = new KeyManager();
    }
    return KeyManager.instance;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    await sodium.ready;
    // Test hash-wasm initialization
    await argon2id({
      password: 'test',
      salt: new Uint8Array(16),
      parallelism: 1,
      iterations: 1,
      memorySize: 1024,
      hashLength: 32,
      outputType: 'binary'
    });
    this.isInitialized = true;
  }

  // Generate a new 32-byte data encryption key
  generateDataEncryptionKey(): Uint8Array {
    this.ensureInitialized();
    return sodium.randombytes_buf(32);
  }

  // Generate a 16-byte salt for Argon2id
  generateSalt(): Uint8Array {
    this.ensureInitialized();
    return sodium.randombytes_buf(16);
  }

  // Derive a key encryption key from passphrase using Argon2id
  async deriveKeyEncryptionKey(passphrase: string, salt: Uint8Array): Promise<Uint8Array> {
    this.ensureInitialized();
    
    const result = await argon2id({
      password: passphrase,
      salt: salt,
      parallelism: 1,
      iterations: 2, // 2 iterations
      memorySize: 64 * 1024, // 64MB memory cost
      hashLength: 32, // 32-byte output
      outputType: 'binary'
    });

    return new Uint8Array(result);
  }

  // Encrypt the DEK with the KEK
  encryptDataEncryptionKey(dek: Uint8Array, kek: Uint8Array): Uint8Array {
    this.ensureInitialized();
    
    const nonce = sodium.randombytes_buf(24); // XChaCha20-Poly1305 nonce
    const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      dek,
      null, // No additional data
      null, // No secret nonce
      nonce,
      kek
    );

    // Return nonce + ciphertext
    const result = new Uint8Array(nonce.length + encrypted.length);
    result.set(nonce);
    result.set(encrypted, nonce.length);
    return result;
  }

  // Decrypt the DEK with the KEK
  decryptDataEncryptionKey(encryptedDek: Uint8Array, kek: Uint8Array): Uint8Array | null {
    this.ensureInitialized();
    
    try {
      const nonce = encryptedDek.slice(0, 24);
      const ciphertext = encryptedDek.slice(24);
      
      const decrypted = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
        null, // No secret nonce
        ciphertext,
        null, // No additional data
        nonce,
        kek
      );

      return decrypted;
    } catch (error) {
      console.error('Failed to decrypt data encryption key:', error);
      return null;
    }
  }

  // Store the DEK in memory (unlocked state)
  setDataEncryptionKey(dek: Uint8Array): void {
    this.dataEncryptionKey = new Uint8Array(dek);
    this.resetLockTimeout();
  }

  // Get the current DEK (null if locked)
  getDataEncryptionKey(): Uint8Array | null {
    if (this.dataEncryptionKey) {
      this.resetLockTimeout();
      return new Uint8Array(this.dataEncryptionKey);
    }
    return null;
  }

  // Check if unlocked
  isUnlocked(): boolean {
    return this.dataEncryptionKey !== null;
  }

  // Lock the application (zeroize DEK)
  lock(): void {
    if (this.dataEncryptionKey) {
      sodium.memzero(this.dataEncryptionKey);
      this.dataEncryptionKey = null;
    }
    
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
      this.lockTimeout = null;
    }
  }

  // Reset the auto-lock timeout (15 minutes of inactivity)
  private resetLockTimeout(): void {
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
    }
    
    this.lockTimeout = setTimeout(() => {
      this.lock();
    }, 15 * 60 * 1000); // 15 minutes
  }

  // Cleanup on unload
  cleanup(): void {
    this.lock();
  }

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error('KeyManager not initialized. Call initialize() first.');
    }
  }
}

// Initialize on module load and setup cleanup
let keyManager: KeyManager | null = null;

export const getKeyManager = async (): Promise<KeyManager> => {
  if (!keyManager) {
    keyManager = KeyManager.getInstance();
    await keyManager.initialize();
    
    // Setup cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        keyManager?.cleanup();
      });
      
      window.addEventListener('unload', () => {
        keyManager?.cleanup();
      });
    }
  }
  
  return keyManager;
};